import { supabase } from "@/integrations/supabase/client";

export type EmailType =
  | "project_created"
  | "project_started"
  | "project_completed"
  | "project_cancelled"
  | "milestone_completed"
  | "payment_received"
  | "payment_released"
  | "refund_processed"
  | "new_message"
  | "contract_created"
  | "contract_signed"
  | "contract_fully_signed"
  | "review_received"
  | "partnership_request"
  | "partnership_accepted"
  | "partnership_declined"
  | "account_welcome"
  | "password_reset";

interface EmailData {
  [key: string]: string | number | boolean | undefined;
}

interface SendEmailParams {
  recipientId: string;
  emailType: EmailType;
  data: EmailData;
  relatedEntityId?: string;
  relatedEntityType?: string;
}

/**
 * Send a transactional email using the centralized email system
 * This respects user email preferences automatically
 */
export async function sendPlatformEmail({
  recipientId,
  emailType,
  data,
  relatedEntityId,
  relatedEntityType,
}: SendEmailParams): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: result, error } = await supabase.functions.invoke("send-platform-email", {
      body: {
        recipientId,
        emailType,
        data,
        relatedEntityId,
        relatedEntityType,
      },
    });

    if (error) {
      console.error("Failed to send platform email:", error);
      return { success: false, error: error.message };
    }

    console.log(`Platform email sent: ${emailType} to ${recipientId}`);
    return { success: true };
  } catch (error: any) {
    console.error("Error sending platform email:", error);
    return { success: false, error: error.message };
  }
}

// ==================== Project Emails ====================

export async function notifyProjectCreated(
  editorId: string,
  clientName: string,
  projectTitle: string,
  projectId: string
) {
  return sendPlatformEmail({
    recipientId: editorId,
    emailType: "project_created",
    data: {
      clientName,
      projectTitle,
      projectUrl: `${window.location.origin}/collaboration/${projectId}`,
    },
    relatedEntityId: projectId,
    relatedEntityType: "project",
  });
}

export async function notifyProjectStarted(
  recipientId: string,
  projectTitle: string,
  editorName: string,
  projectId: string
) {
  return sendPlatformEmail({
    recipientId,
    emailType: "project_started",
    data: {
      projectTitle,
      editorName,
      projectUrl: `${window.location.origin}/collaboration/${projectId}`,
    },
    relatedEntityId: projectId,
    relatedEntityType: "project",
  });
}

export async function notifyProjectCompleted(
  recipientId: string,
  projectTitle: string,
  projectId: string
) {
  return sendPlatformEmail({
    recipientId,
    emailType: "project_completed",
    data: {
      projectTitle,
      projectUrl: `${window.location.origin}/collaboration/${projectId}`,
    },
    relatedEntityId: projectId,
    relatedEntityType: "project",
  });
}

export async function notifyProjectCancelled(
  recipientId: string,
  projectTitle: string,
  reason: string
) {
  return sendPlatformEmail({
    recipientId,
    emailType: "project_cancelled",
    data: {
      projectTitle,
      reason,
    },
    relatedEntityType: "project",
  });
}

// ==================== Milestone Emails ====================

export async function notifyMilestoneCompleted(
  clientId: string,
  milestoneTitle: string,
  projectTitle: string,
  projectId: string
) {
  return sendPlatformEmail({
    recipientId: clientId,
    emailType: "milestone_completed",
    data: {
      milestoneTitle,
      projectTitle,
      projectUrl: `${window.location.origin}/collaboration/${projectId}`,
    },
    relatedEntityId: projectId,
    relatedEntityType: "project",
  });
}

// ==================== Payment Emails ====================

export async function notifyPaymentReceived(
  editorId: string,
  amount: number,
  projectTitle: string,
  projectId: string
) {
  return sendPlatformEmail({
    recipientId: editorId,
    emailType: "payment_received",
    data: {
      amount: amount.toFixed(2),
      projectTitle,
      projectUrl: `${window.location.origin}/collaboration/${projectId}`,
    },
    relatedEntityId: projectId,
    relatedEntityType: "payment",
  });
}

export async function notifyPaymentReleased(
  editorId: string,
  amount: number,
  projectTitle: string
) {
  return sendPlatformEmail({
    recipientId: editorId,
    emailType: "payment_released",
    data: {
      amount: amount.toFixed(2),
      projectTitle,
    },
    relatedEntityType: "payment",
  });
}

export async function notifyRefundProcessed(
  clientId: string,
  amount: number,
  projectTitle: string,
  reason: string
) {
  return sendPlatformEmail({
    recipientId: clientId,
    emailType: "refund_processed",
    data: {
      amount: amount.toFixed(2),
      projectTitle,
      reason,
    },
    relatedEntityType: "refund",
  });
}

// ==================== Message Emails ====================

export async function notifyNewMessage(
  recipientId: string,
  senderName: string,
  messagePreview: string,
  conversationUrl: string
) {
  return sendPlatformEmail({
    recipientId,
    emailType: "new_message",
    data: {
      senderName,
      messagePreview: messagePreview.substring(0, 100),
      conversationUrl,
    },
    relatedEntityType: "message",
  });
}

// ==================== Contract Emails ====================

export async function notifyContractCreated(
  recipientId: string,
  contractTitle: string,
  projectTitle: string,
  contractId: string
) {
  return sendPlatformEmail({
    recipientId,
    emailType: "contract_created",
    data: {
      contractTitle,
      projectTitle,
      contractUrl: `${window.location.origin}/contracts/${contractId}`,
    },
    relatedEntityId: contractId,
    relatedEntityType: "contract",
  });
}

export async function notifyContractSigned(
  recipientId: string,
  contractTitle: string,
  signerName: string,
  contractId: string
) {
  return sendPlatformEmail({
    recipientId,
    emailType: "contract_signed",
    data: {
      contractTitle,
      signerName,
      contractUrl: `${window.location.origin}/contracts/${contractId}`,
    },
    relatedEntityId: contractId,
    relatedEntityType: "contract",
  });
}

export async function notifyContractFullySigned(
  recipientId: string,
  contractTitle: string,
  contractId: string
) {
  return sendPlatformEmail({
    recipientId,
    emailType: "contract_fully_signed",
    data: {
      contractTitle,
      contractUrl: `${window.location.origin}/contracts/${contractId}`,
    },
    relatedEntityId: contractId,
    relatedEntityType: "contract",
  });
}

// ==================== Review Emails ====================

export async function notifyReviewReceived(
  editorId: string,
  clientName: string,
  rating: number,
  projectTitle: string,
  projectId: string
) {
  return sendPlatformEmail({
    recipientId: editorId,
    emailType: "review_received",
    data: {
      clientName,
      rating,
      projectTitle,
      projectUrl: `${window.location.origin}/collaboration/${projectId}`,
    },
    relatedEntityId: projectId,
    relatedEntityType: "review",
  });
}

// ==================== Partnership Emails ====================

export async function notifyPartnershipRequest(
  editorId: string,
  clientName: string,
  partnershipName: string,
  partnershipId: string
) {
  return sendPlatformEmail({
    recipientId: editorId,
    emailType: "partnership_request",
    data: {
      clientName,
      partnershipName,
      partnershipUrl: `${window.location.origin}/partnerships/${partnershipId}`,
    },
    relatedEntityId: partnershipId,
    relatedEntityType: "partnership",
  });
}

export async function notifyPartnershipAccepted(
  clientId: string,
  editorName: string,
  partnershipName: string,
  partnershipId: string
) {
  return sendPlatformEmail({
    recipientId: clientId,
    emailType: "partnership_accepted",
    data: {
      editorName,
      partnershipName,
      partnershipUrl: `${window.location.origin}/partnerships/${partnershipId}`,
    },
    relatedEntityId: partnershipId,
    relatedEntityType: "partnership",
  });
}

export async function notifyPartnershipDeclined(
  clientId: string,
  editorName: string,
  partnershipName: string
) {
  return sendPlatformEmail({
    recipientId: clientId,
    emailType: "partnership_declined",
    data: {
      editorName,
      partnershipName,
    },
    relatedEntityType: "partnership",
  });
}

// ==================== Account Emails ====================

export async function sendWelcomeEmail(userId: string, userName: string) {
  return sendPlatformEmail({
    recipientId: userId,
    emailType: "account_welcome",
    data: {
      userName,
      dashboardUrl: `${window.location.origin}/dashboard`,
    },
    relatedEntityType: "account",
  });
}
